import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Edit3, Loader2, X } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import portalApi from "@/lib/portalApi";
import { useStartupPortalAuth } from "@/contexts/StartupPortalAuthContext";

const Section = ({ title, eyebrow, accent = "sage", children, testId }) => (
  <motion.section
    initial={{ opacity: 0, y: 8 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.05 }}
    transition={{ duration: 0.4, ease: "easeOut" }}
    className="rounded-3xl border border-sandBeige bg-white p-6 sm:p-7 shadow-soft relative overflow-hidden"
    data-testid={testId}
  >
    <div
      className={`absolute top-0 left-0 w-16 h-1.5 rounded-bl-3xl rounded-tr-3xl ${
        accent === "sage"
          ? "bg-sageMint"
          : accent === "sky"
          ? "bg-skyMist"
          : accent === "rose"
          ? "bg-roseMilk"
          : "bg-butterCream"
      }`}
      aria-hidden="true"
    />
    {eyebrow && <p className="wit-eyebrow text-raspberry mb-2">{eyebrow}</p>}
    <h2 className="font-serif text-xl text-cocoa mb-5">{title}</h2>
    {children}
  </motion.section>
);

const Field = ({ label, children, testId }) => (
  <div className="flex flex-col gap-1.5" data-testid={testId}>
    <label className="text-xs font-medium text-warmGrey uppercase tracking-wide">{label}</label>
    {children}
  </div>
);

const TextInput = ({ value, onChange, type = "text", inputMode, placeholder, autoComplete, testId }) => (
  <input
    type={type}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    inputMode={inputMode}
    placeholder={placeholder}
    autoComplete={autoComplete}
    className="bg-porcelain border border-sandBeige rounded-2xl px-4 py-3 focus:border-raspberry focus:outline-none text-sm text-cocoa transition-colors"
    data-testid={testId}
  />
);

const TextArea = ({ value, onChange, placeholder, rows = 4, testId }) => (
  <textarea
    rows={rows}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="bg-porcelain border border-sandBeige rounded-2xl px-4 py-3 focus:border-raspberry focus:outline-none text-sm text-cocoa resize-none transition-colors"
    data-testid={testId}
  />
);

const ROW_TOGGLE = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 text-sm">
    <span className="text-warmGrey">{label}</span>
    <span className={value ? "text-raspberry font-medium" : "text-warmGrey"}>{value ? "✓" : "—"}</span>
  </div>
);

export default function PortalProfile() {
  const { t } = useTranslation();
  const { session, refreshSession } = useStartupPortalAuth();
  const me = session?.me || {};
  const company = me.company || {};
  const details = me.details || {};
  const funding = me.funding || {};
  const founderSelf = me.founder_self || {};

  // Local form state — initialized from session
  const [form, setForm] = useState({
    company: { ...company },
    details: { ...details },
    funding: { ...funding },
    founder_self: { ...founderSelf },
  });
  const [saving, setSaving] = useState(null); // section name being saved

  const updateSection = (section, key, val) =>
    setForm((f) => ({ ...f, [section]: { ...f[section], [key]: val } }));

  const save = async (sectionKey, payload) => {
    setSaving(sectionKey);
    try {
      await portalApi.patch("/me", { [sectionKey]: payload });
      await refreshSession();
      toast.success(t("portal.profile.saved"));
    } catch (_e) {
      toast.error(t("portal.profile.saveFailed"));
    } finally {
      setSaving(null);
    }
  };

  const SaveBar = ({ sectionKey, payload }) => (
    <div className="flex items-center justify-end gap-2 mt-5 pt-5 border-t border-sandBeige">
      <button
        type="button"
        onClick={() => save(sectionKey, payload)}
        disabled={saving === sectionKey}
        className="bg-raspberry hover:bg-coral disabled:opacity-60 disabled:hover:bg-raspberry text-white text-sm rounded-full px-5 py-2.5 inline-flex items-center gap-2 transition-colors"
        data-testid={`save-${sectionKey}`}
      >
        {saving === sectionKey ? (
          <>
            <Loader2 size={14} className="animate-spin" /> {t("portal.profile.saving")}
          </>
        ) : (
          <>
            <Check size={14} /> {t("portal.profile.save")}
          </>
        )}
      </button>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto flex flex-col gap-6" data-testid="portal-profile">
      <motion.header
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <p className="wit-eyebrow text-raspberry mb-2">{t("portal.profile.eyebrow")}</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-cocoa leading-[1.05]">
          {t("portal.profile.title")}
        </h1>
        <p className="mt-3 text-warmGrey">{t("portal.profile.lead")}</p>
      </motion.header>

      {/* Company */}
      <Section title={t("portal.profile.companyTitle")} eyebrow={t("portal.profile.companyEyebrow")} accent="sage" testId="section-company">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("portal.fields.company_name")} testId="field-company-name">
            <div className="bg-ivory border border-sandBeige rounded-2xl px-4 py-3 text-sm text-cocoa flex items-center gap-2" data-testid="locked-company-name">
              <span className="flex-1">{company.name || "—"}</span>
              <span className="text-[10px] uppercase text-warmGrey tracking-wide">{t("portal.profile.locked")}</span>
            </div>
          </Field>
          <Field label={t("portal.fields.website")}>
            <TextInput
              value={form.company.website}
              onChange={(v) => updateSection("company", "website", v)}
              type="url"
              placeholder="https://"
              inputMode="url"
              autoComplete="url"
              testId="input-website"
            />
          </Field>
          <Field label={t("portal.fields.founded_year")}>
            <TextInput
              value={form.company.founded_year}
              onChange={(v) =>
                updateSection(
                  "company",
                  "founded_year",
                  v ? parseInt(v, 10) || null : null,
                )
              }
              type="number"
              inputMode="numeric"
              testId="input-founded"
            />
          </Field>
          <Field label={t("portal.fields.country_city")}>
            <div className="grid grid-cols-2 gap-2">
              <TextInput
                value={form.company.country}
                onChange={(v) => updateSection("company", "country", v)}
                placeholder={t("portal.profile.country")}
                testId="input-country"
              />
              <TextInput
                value={form.company.city}
                onChange={(v) => updateSection("company", "city", v)}
                placeholder={t("portal.profile.city")}
                testId="input-city"
              />
            </div>
          </Field>
          <div className="sm:col-span-2">
            <Field label={t("portal.fields.one_liner")}>
              <TextInput
                value={form.company.one_liner}
                onChange={(v) => updateSection("company", "one_liner", v)}
                placeholder={t("portal.profile.oneLinerHint")}
                testId="input-oneliner"
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label={t("portal.fields.description")}>
              <TextArea
                value={form.company.description}
                onChange={(v) => updateSection("company", "description", v)}
                rows={5}
                placeholder={t("portal.profile.descriptionHint")}
                testId="input-description"
              />
            </Field>
          </div>
        </div>
        <SaveBar
          sectionKey="company"
          payload={form.company}
        />
      </Section>

      {/* Founder self */}
      <Section title={t("portal.profile.founderTitle")} eyebrow={t("portal.profile.founderEyebrow")} accent="rose" testId="section-founder">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("portal.fields.first_name")}>
            <TextInput
              value={form.founder_self.first_name}
              onChange={(v) => updateSection("founder_self", "first_name", v)}
              autoComplete="given-name"
              testId="input-firstname"
            />
          </Field>
          <Field label={t("portal.fields.last_name")}>
            <TextInput
              value={form.founder_self.last_name}
              onChange={(v) => updateSection("founder_self", "last_name", v)}
              autoComplete="family-name"
              testId="input-lastname"
            />
          </Field>
          <Field label={t("portal.fields.email")}>
            <div className="bg-ivory border border-sandBeige rounded-2xl px-4 py-3 text-sm text-cocoa flex items-center gap-2" data-testid="locked-email">
              <span className="flex-1 truncate">{founderSelf.email}</span>
              <span className="text-[10px] uppercase text-warmGrey tracking-wide">{t("portal.profile.locked")}</span>
            </div>
          </Field>
          <Field label={t("portal.fields.role")}>
            <TextInput
              value={form.founder_self.role}
              onChange={(v) => updateSection("founder_self", "role", v)}
              testId="input-role"
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label={t("portal.fields.linkedin")}>
              <TextInput
                value={form.founder_self.linkedin}
                onChange={(v) => updateSection("founder_self", "linkedin", v)}
                type="url"
                placeholder="https://linkedin.com/in/…"
                testId="input-linkedin"
              />
            </Field>
          </div>
        </div>
        <SaveBar
          sectionKey="founder_self"
          payload={form.founder_self}
        />
      </Section>

      {/* Details */}
      <Section title={t("portal.profile.detailsTitle")} eyebrow={t("portal.profile.detailsEyebrow")} accent="sage" testId="section-details">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("portal.fields.stage")}>
            <TextInput
              value={form.details.stage}
              onChange={(v) => updateSection("details", "stage", v)}
              testId="input-stage"
            />
          </Field>
          <Field label={t("portal.fields.team_size")}>
            <TextInput
              value={form.details.team_size}
              onChange={(v) =>
                updateSection("details", "team_size", v ? parseInt(v, 10) || null : null)
              }
              type="number"
              inputMode="numeric"
              testId="input-team-size"
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label={t("portal.fields.problem")}>
              <TextArea
                value={form.details.problem}
                onChange={(v) => updateSection("details", "problem", v)}
                rows={3}
                testId="input-problem"
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label={t("portal.fields.solution")}>
              <TextArea
                value={form.details.solution}
                onChange={(v) => updateSection("details", "solution", v)}
                rows={3}
                testId="input-solution"
              />
            </Field>
          </div>
        </div>
        <SaveBar
          sectionKey="details"
          payload={form.details}
        />
      </Section>

      {/* Funding */}
      <Section title={t("portal.profile.fundingTitle")} eyebrow={t("portal.profile.fundingEyebrow")} accent="sky" testId="section-funding">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("portal.fields.ask_amount")}>
            <TextInput
              value={form.funding.ask_amount}
              onChange={(v) =>
                updateSection("funding", "ask_amount", v ? parseFloat(v) || null : null)
              }
              type="number"
              inputMode="decimal"
              testId="input-ask"
            />
          </Field>
          <Field label={t("portal.fields.mrr")}>
            <TextInput
              value={form.funding.mrr}
              onChange={(v) =>
                updateSection("funding", "mrr", v ? parseFloat(v) || null : null)
              }
              type="number"
              inputMode="decimal"
              testId="input-mrr"
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label={t("portal.fields.use_of_funds")}>
              <TextArea
                value={form.funding.use_of_funds}
                onChange={(v) => updateSection("funding", "use_of_funds", v)}
                rows={3}
                testId="input-use-of-funds"
              />
            </Field>
          </div>
        </div>
        <SaveBar
          sectionKey="funding"
          payload={form.funding}
        />
      </Section>
    </div>
  );
}
